import { Box, Tooltip } from '@mui/material';
import { type ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState } from 'recoil';
import {
    MAX_DIAMETER_MM,
    MIN_DIAMETER_MM,
} from '~/engine/tool/tools/contouring-tools/CircleCursorStrokeTool';
import {
    MAX_SMOOTH_STRENGTH,
    MIN_SMOOTH_STRENGTH,
} from '~/engine/tool/tools/contouring-tools/SmoothBrushTool';
import { StrokeMode, smoothBrushOptionsState } from '~/state/atoms/toolsAtoms';
import { cmToMm, mmToCm } from '~/utils/units';
import ToolTrayModeToggle from './ToolTrayModeToggle';
import ToolTraySlider from './ToolTraySlider';

interface SmoothBrushToolOptionsProps {
    /** Switches shown after the mode, before the sliders. */
    children?: ReactNode;
}

export default function SmoothBrushToolOptions({
    children,
}: SmoothBrushToolOptionsProps) {
    const intl = useIntl();
    const [smoothBrushOptions, setSmoothBrushOptions] = useRecoilState(
        smoothBrushOptionsState
    );

    const handleSetDiameter = (diameterCm: number) => {
        const newDiameter = cmToMm(diameterCm);
        if (newDiameter < MIN_DIAMETER_MM || newDiameter > MAX_DIAMETER_MM) {
            return;
        }
        setSmoothBrushOptions((options) => ({
            ...options,
            diameter: newDiameter,
        }));
    };

    const handleSetStrength = (strength: number) => {
        if (strength < MIN_SMOOTH_STRENGTH || strength > MAX_SMOOTH_STRENGTH) {
            return;
        }
        setSmoothBrushOptions((options) => ({
            ...options,
            strength,
        }));
    };

    const handleSetMode = (newMode: StrokeMode) => {
        setSmoothBrushOptions((options) => ({
            ...options,
            mode: newMode,
        }));
    };

    const diameterCm = mmToCm(smoothBrushOptions.diameter);

    return (
        <>
            <ToolTrayModeToggle
                value={smoothBrushOptions.mode}
                onChange={handleSetMode}
                testIdPrefix="smooth-brush-mode"
            />
            {children}
            <Tooltip
                title={intl.formatMessage({
                    id: 'toolOptions.diameterWheelHint',
                })}
            >
                <Box sx={{ display: 'flex', flex: '1 1 100%', minWidth: 0 }}>
                    <ToolTraySlider
                        label={intl.formatMessage({
                            id: 'toolOptions.diameter',
                        })}
                        value={diameterCm}
                        onChange={handleSetDiameter}
                        step={0.1}
                        min={mmToCm(MIN_DIAMETER_MM)}
                        max={mmToCm(MAX_DIAMETER_MM)}
                        valueText={diameterCm.toFixed(1)}
                        unit="cm"
                    />
                </Box>
            </Tooltip>
            {/* Continuous strength: the per-pass sigma interpolates
                between the strength table entries, so no steps or
                numeric value are surfaced. */}
            <ToolTraySlider
                label={intl.formatMessage({ id: 'toolOptions.strength' })}
                value={smoothBrushOptions.strength}
                onChange={handleSetStrength}
                step={0.1}
                min={MIN_SMOOTH_STRENGTH}
                max={MAX_SMOOTH_STRENGTH}
                data-testid="smooth-brush-strength"
            />
        </>
    );
}
